/**
 * Bell sheet: the unread dot is the only indicator; the sheet lists what is new, one fact per line. Clear all beside the
 * title and a clear button on each row hide notices (D-091, FigCraft page 54); Undo brings the last clearing back.
 * Opening it reads everything (the dots stay until it closes), so there is no separate "Mark all as read" (audit #43).
 * On desktop it is a popover under the bell instead of a bottom sheet.
 */
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Avatar, Button, EmptyState, Hairline, Icon, Sheet, Slab, TextLink } from "../components";
import { useSessionRefresh } from "../lib/session";
import { useFocusTrap } from "../components/Sheet";
import { useDesktop } from "../lib/useDesktop";
import { api } from "../lib/api";
import { formatNumber, relativeTime } from "../lib/format";
import { joinInvite } from "../lib/invites";
import { errorSentence } from "../lib/api";
import { useNotifications } from "../lib/notifications";
import type { NotificationsView } from "../lib/types";
import { useLoad } from "../lib/useLoad";
import { isWatchNotice } from "../lib/noticeActions";
import { WatchNoticeActions } from "../components/WatchNoticeActions";
import { chime } from "../lib/chimes";

type Item = NotificationsView["items"][number];
/** How long Undo stays after a clear. */
export const UNDO_MS = 6000;

/** D-196: the bell's list (the bell is back beside your tokens; your picture opens the You menu instead). */
export function NotificationsSheet({ open, onClose, onRead }: { open: boolean; onClose: () => void; onRead?: () => void }): ReactElement | null {
  const [clearAll, setClearAll] = useState<(() => void) | null>(null);
  const desktop = useDesktop();
  const action = open && clearAll ? <TextLink onClick={clearAll}>Clear all</TextLink> : undefined;
  const body = open && <NotificationsBody onClose={onClose} onRead={onRead} onClearAll={setClearAll} />;
  if (desktop) return open ? <HeaderPopover anchor="home-bell" title="What's new" onClose={onClose} action={action} testId="notif-popover">{body}</HeaderPopover> : null;
  return <Sheet open={open} onClose={onClose} title="What's new" action={action}>{body}</Sheet>;
}

/** Desktop: a panel hanging from a header button (the bell, your picture), closed by Escape or a click outside. */
export function HeaderPopover({ anchor, title, sub, onClose, action, children, testId }: { anchor: string; title: string; sub?: ReactNode; onClose: () => void; action?: ReactNode; children: ReactNode; testId: string }): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, true, onClose);
  const [pos] = useState(() => {
    const at = [...document.querySelectorAll<HTMLElement>(`[data-testid='${anchor}']`)].map((b) => b.getBoundingClientRect()).find((r) => r.width > 0);
    // D-199: never wider than the window, and never past its left edge (sideways phones opened it off screen).
    const width = Math.min(420, window.innerWidth - 32);
    const right = at && at.width > 0 ? Math.max(16, window.innerWidth - at.right) : 24;
    return { top: at && at.width > 0 ? at.bottom + 10 : 76, right: Math.min(right, window.innerWidth - width - 16), width };
  });
  return createPortal(
    <>
      <div className="notif-popover__scrim" onClick={onClose} data-testid="scrim" />
      <div ref={ref} className="notif-popover" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} style={{ top: pos.top, right: pos.right, width: pos.width }} data-testid={testId}>
        <div className="sheet__head"><div className="sheet__title-row"><h2 className="sheet__title">{title}</h2>{action}</div>{sub ? <p className="small text-secondary">{sub}</p> : null}<Hairline /></div>
        <div className="notif-popover__body">{children}</div>
      </div>
    </>,
    document.body,
  );
}
function NotificationsBody({ onClose, onRead, onClearAll }: { onClose: () => void; onRead?: () => void; onClearAll: (f: (() => void) | null) => void }): ReactElement {
  const navigate = useNavigate();
  const { refresh } = useNotifications();
  const { data, error, loading, setData, reload } = useLoad(() => api.get<NotificationsView>("/notifications"));
  const [joining, setJoining] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ id: string; text: string } | null>(null);
  const [undo, setUndo] = useState<{ items: Item[]; text: string } | null>(null);
  const [acting, setActing] = useState<string | null>(null);
  const refreshSession = useSessionRefresh();
  // D-193: answer a friend or club request in the row; the row then says what happened, and the tab counts update.
  const act = async (n: Item, accept: boolean) => {
    setActing(n.id); setFailed(null);
    try {
      const r = await api.post<{ outcome: string }>(`/notifications/${n.id}/act`, { accept });
      setData((d) => (d ? { items: d.items.map((x) => (x.id === n.id ? { ...x, outcome: r.outcome, actions: undefined, read: true } : x)) } : d));
      if (accept) chime("celebrate");
      void refreshSession(); refresh();
    } catch (e) { setFailed({ id: n.id, text: errorSentence(e) }); }
    finally { setActing(null); }
  };
  // Seeing the list reads it: the bell count clears at once; the dots stay on what was new until the list closes.
  const anyUnread = !!data?.items.some((n) => !n.read);
  const readOnce = useRef(false);
  useEffect(() => { if (!anyUnread || readOnce.current) return; readOnce.current = true; onRead?.(); void api.post("/notifications/read").then(() => refresh(), () => undefined); }, [anyUnread]); // eslint-disable-line react-hooks/exhaustive-deps
  // Clearing hides at once; the server call follows, and a failure puts the rows back.
  const clear = async (items: Item[]) => {
    if (items.length === 0) return;
    const ids = new Set(items.map((n) => n.id));
    setData((d) => (d ? { items: d.items.filter((n) => !ids.has(n.id)) } : d));
    setUndo({ items, text: items.length === 1 ? "Cleared." : `Cleared ${formatNumber(items.length)} notifications.` });
    try { await api.post("/notifications/clear", { ids: [...ids] }); refresh(); onRead?.(); }
    catch { setUndo(null); void reload(); }
  };
  const restore = async () => {
    if (!undo) return;
    const back = undo.items; setUndo(null);
    try { await api.post("/notifications/restore", { ids: back.map((n) => n.id) }); } finally { void reload(); refresh(); }
  };
  const items = data?.items ?? [];
  // The sheet's Clear all (beside the title) clears whatever is listed when it is tapped.
  const clearListed = useRef(() => undefined as void); clearListed.current = () => void clear(items);
  const any = items.length > 0;
  useEffect(() => { onClearAll(any ? () => () => clearListed.current() : null); return () => onClearAll(null); }, [any, onClearAll]);
  useEffect(() => { if (!undo) return; const t = setTimeout(() => setUndo(null), UNDO_MS); return () => clearTimeout(t); }, [undo]);
  // A table invite joins first (the lobby only shows seated players); anything else just opens its page.
  const open = async (n: Item) => {
    if (n.kind !== "table-invite" || !n.href) { onClose(); if (n.href) navigate(n.href); return; }
    setJoining(n.id); setFailed(null);
    try { const to = await joinInvite(n.href.split("/").pop() ?? ""); onClose(); navigate(to); }
    catch (e) { setFailed({ id: n.id, text: errorSentence(e) }); }
    finally { setJoining(null); }
  };
  const undoBar = undo && (
    <div className="notif-undo" role="status" data-testid="notif-undo">
      <Icon name="check" size={18} /><span className="notif-undo__text">{undo.text}</span>
      <TextLink onClick={() => void restore()}>Undo</TextLink>
    </div>
  );
  if (error) return <EmptyState title="Couldn't load this" body={error} />;
  if (loading || !data) return <p className="small text-muted center">Loading…</p>;
  if (items.length === 0) return <>{undoBar}<EmptyState title="You're all caught up." body="Table invites, friend requests, tournament reminders and transfers land here." /></>;
  // D-193: Today and Earlier, each its own list.
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  // D-196: what waits on an admin comes first (admins only ever get these), then Today and Earlier.
  const adminItems = items.filter((n) => n.kind.startsWith("admin-")), rest = items.filter((n) => !n.kind.startsWith("admin-"));
  const groups = [
    { title: `Needs an admin · ${formatNumber(adminItems.length)}`, admin: true, items: adminItems },
    { title: "Today", admin: false, items: rest.filter((n) => n.at >= startOfToday.getTime()) },
    { title: "Earlier", admin: false, items: rest.filter((n) => n.at < startOfToday.getTime()) },
  ].filter((g) => g.items.length > 0);
  const row = (n: Item) => {
    const can = (a: NonNullable<Item["actions"]>[number]) => n.actions?.includes(a);
    const answer = (n.kind === "friend-request" || n.kind === "club-request") && (can("accept") || can("let-in"));
    return (
      <div key={n.id} className={`notif-row ${n.read ? "notif-row--read" : ""}`} data-testid={`notif-${n.kind}`}>
        <button type="button" className="notif-row__open" onClick={() => void open(n)} aria-busy={joining === n.id || undefined}>
          <span className="notif-row__pic" aria-hidden="true">
            {n.from ? <Avatar publicId={n.from.publicId} photoVersion={n.from.photoVersion} name={n.from.name} size={40} /> : <span className="notif-row__disc"><Icon name={KIND_ICON[n.kind] ?? "bell"} size={20} /></span>}
            {n.from ? <span className="notif-row__kind"><Icon name={KIND_ICON[n.kind] ?? "bell"} size={12} /></span> : null}
          </span>
          <span><span className="notif-row__text">{n.from && n.text.startsWith(n.from.name) ? <><span className="notif-row__name">{n.from.name}</span>{n.text.slice(n.from.name.length)}</> : n.text}</span><span className="notif-row__time">{relativeTime(n.at)}</span>
            {n.outcome && <span className="small notif-row__outcome" role="status"><Icon name="check" size={14} />{n.outcome}</span>}
            {failed?.id === n.id && <span className="small notif-row__error">{failed.text}</span>}</span>
          {n.kind === "table-invite" && <span className="notif-row__action">{joining === n.id ? "Joining…" : "Join"}</span>}
          {n.kind.startsWith("admin-") && <span className="notif-row__action">{n.kind === "admin-report" ? "Review" : "Open"}</span>}
        </button>
        {answer && (
          <div className="notif-row__answer">
            <Button size={44} full={false} busy={acting === n.id} onClick={() => void act(n, true)}>{can("let-in") ? "Let in" : "Accept"}</Button>
            <Button size={44} full={false} kind="quiet" onClick={() => void act(n, false)}>Decline</Button>
          </div>
        )}
        {isWatchNotice(n.kind) && <WatchNoticeActions item={n} onGo={(to) => { onClose(); navigate(to); }} />}
        <button type="button" className="notif-row__clear" aria-label={`Clear: ${n.text}`} onClick={() => void clear([n])}><Icon name="x" size={18} /></button>
      </div>
    );
  };
  return (
    <>
      {undoBar}
      {groups.map((g) => (
        <section key={g.title} className="notif-day" aria-label={g.title}>
          <h4 className={`cap-b notif-day__title${g.admin ? " notif-day__title--admin" : " text-secondary"}`}>{g.admin && <Icon name="shield-check" size={14} />}{g.title}</h4>
          <Slab aria-label={`Notifications, ${g.title.toLowerCase()}`}>{g.items.map(row)}</Slab>
        </section>
      ))}
    </>
  );
}

/** The small badge on each picture, and the disc for notices that aren't about a person. */
const KIND_ICON: Partial<Record<Item["kind"], string>> = {
  "friend-request": "user-plus", "club-request": "crest", "table-invite": "layers", transfer: "coins", tournament: "trophy",
  "seat-request": "eye", "seat-offer": "eye", "friend-table": "eye", info: "bell",
  "admin-report": "triangle-alert", "admin-verification": "badge-check", "admin-prize": "trophy", "admin-support": "mail",
};
